import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { Chip, ChipGroup } from '@/components/atoms/Chip';
import {
  STUDY_TIME_BANDS,
  STUDY_TIME_HOURS,
  type StudyTimeBand,
  type StudyTimeSelection,
  type StudyTimeSelectorHandle,
  type StudyTimeSelectorProps,
} from './StudyTimeSelector.types';
import styles from './StudyTimeSelector.module.scss';

/**
 * Selector de horario de estudio en dos pasos visibles:
 *   1. Banda del día (mañana / tarde / noche).
 *   2. Hora concreta dentro de esa banda — aparece debajo cuando el
 *      alumno elige una banda.
 *
 * Mismo patrón visual (cards con radio) que [[level-selector]] y
 * [[weekly-routine-selector]]. La validez exige que ambas elecciones
 * estén hechas; el padre persiste el valor (`reminderTime`) cuando le
 * toque (arrow del dialog).
 *
 * Si llega un `defaultHour` (típicamente la hora ya configurada del
 * alumno), buscamos su banda y arrancamos con ambos preseleccionados.
 */
function findBandForHour(hour: string | undefined): StudyTimeBand | null {
  if (!hour) return null;
  for (const band of STUDY_TIME_BANDS) {
    if (STUDY_TIME_HOURS[band].includes(hour)) return band;
  }
  return null;
}

export const StudyTimeSelector = forwardRef<
  StudyTimeSelectorHandle,
  StudyTimeSelectorProps
>(function StudyTimeSelector(
  { defaultHour, disabled = false, onValidityChange },
  ref
) {
  const { t } = useTranslation();
  const initialBand = useMemo(() => findBandForHour(defaultHour), [defaultHour]);
  const [band, setBand] = useState<StudyTimeBand | null>(initialBand);
  const [hour, setHour] = useState<string | null>(
    initialBand ? (defaultHour ?? null) : null
  );

  const lastNotifiedValidRef = useRef<boolean | null>(null);
  useEffect(() => {
    const isValid = band !== null && hour !== null;
    if (lastNotifiedValidRef.current === isValid) return;
    lastNotifiedValidRef.current = isValid;
    onValidityChange?.(isValid);
  }, [band, hour, onValidityChange]);

  useImperativeHandle(
    ref,
    () => ({
      getSelected: (): StudyTimeSelection | null =>
        band != null && hour != null ? { band, hour } : null,
      isValid: () => band !== null && hour !== null,
    }),
    [band, hour]
  );

  const handleBandChange = (next: string) => {
    const nextBand = next as StudyTimeBand;
    setBand(nextBand);
    // Si la hora actual no pertenece a la nueva banda, la reseteamos
    // para forzar al alumno a elegir una válida dentro del rango.
    if (hour != null && !STUDY_TIME_HOURS[nextBand].includes(hour)) {
      setHour(null);
    }
  };

  const hourOptions = band != null ? STUDY_TIME_HOURS[band] : [];

  return (
    <div className={styles.selector}>
      <RadioGroupPrimitive.Root
        className={styles.selector__list}
        value={band ?? undefined}
        onValueChange={handleBandChange}
        disabled={disabled}
        aria-label={t('studyTimeSelector.bandsAriaLabel')}
      >
        {STUDY_TIME_BANDS.map((b) => {
          const id = `study-band-${b}`;
          return (
            <label key={b} htmlFor={id} className={styles.selector__card}>
              <RadioGroupPrimitive.Item
                value={b}
                id={id}
                className={styles.selector__radio}
                data-no-press-scale=""
              >
                <RadioGroupPrimitive.Indicator
                  className={styles.selector__indicator}
                />
              </RadioGroupPrimitive.Item>
              <span className={styles.selector__label}>
                {t(`studyTimeSelector.bands.${b}`)}
              </span>
            </label>
          );
        })}
      </RadioGroupPrimitive.Root>

      {band != null && (
        <div className={styles.selector__hoursSection}>
          <p className={styles.selector__hoursTitle}>
            {t('studyTimeSelector.hoursTitle')}
          </p>
          <ChipGroup
            selectionMode="single"
            value={hour ?? ''}
            onValueChange={(v) => setHour(v || null)}
            disabled={disabled}
            aria-label={t('studyTimeSelector.hoursAriaLabel')}
          >
            {hourOptions.map((h) => (
              <Chip key={h} value={h}>
                {h}
              </Chip>
            ))}
          </ChipGroup>
        </div>
      )}

      <p className={styles.helper}>{t('studyTimeSelector.helper')}</p>
    </div>
  );
});

StudyTimeSelector.displayName = 'StudyTimeSelector';
